import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  useCreateBadgeTypeMutation,
  useUpdateBadgeTypeMutation,
} from '@/client/api/badges/badges.mutation';
import type { BadgeType } from '@/client/types/badges';
import BadgeRulesPanel from './BadgeRulesPanel';

interface BadgeTypeFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  badgeType?: BadgeType | null;
}

const emptyForm = {
  name: '',
  name_system: '',
  description: '',
  category: '',
  tier: '',
  scope: 'both',
  award_method: 'application',
  requires_verification: true,
  active: true,
};

type FormState = typeof emptyForm;

const inputCls =
  'w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

const BadgeTypeFormModal = ({ isOpen, onClose, badgeType }: BadgeTypeFormModalProps) => {
  const [form, setForm] = useState<FormState>(emptyForm);
  const createMutation = useCreateBadgeTypeMutation();
  const updateMutation = useUpdateBadgeTypeMutation();
  const isPending = createMutation.isPending || updateMutation.isPending;
  const isEdit = Boolean(badgeType?.btid);

  useEffect(() => {
    if (!isOpen) return;
    if (badgeType) {
      setForm({
        name: String(badgeType.name ?? ''),
        name_system: String(badgeType.name_system ?? ''),
        description: String(badgeType.description ?? ''),
        category: String(badgeType.category ?? ''),
        tier: String(badgeType.tier ?? ''),
        scope: String(badgeType.scope ?? 'both'),
        award_method: String(badgeType.award_method ?? 'application'),
        requires_verification: Boolean(
          badgeType.requires_verification === true || badgeType.requires_verification === 1,
        ),
        active: !(badgeType.active === 0 || badgeType.active === false),
      });
    } else {
      setForm(emptyForm);
    }
  }, [isOpen, badgeType]);

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleClose = () => {
    if (isPending) return;
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }

    const payload = {
      name: form.name.trim(),
      name_system: form.name_system.trim() || undefined,
      description: form.description.trim() || undefined,
      category: form.category.trim() || undefined,
      tier: form.tier.trim() || undefined,
      scope: form.scope,
      award_method: form.award_method,
      requires_verification: form.requires_verification ? 1 : 0,
      active: form.active ? 1 : 0,
    };

    const toastId = toast.loading(isEdit ? 'Updating badge type…' : 'Creating badge type…');
    try {
      if (isEdit && badgeType) {
        await updateMutation.mutateAsync({ btid: badgeType.btid, ...payload });
        toast.success('Badge type updated', { id: toastId });
      } else {
        await createMutation.mutateAsync(payload);
        toast.success('Badge type created', { id: toastId });
      }
      onClose();
    } catch (error: any) {
      toast.error(isEdit ? 'Update failed' : 'Create failed', {
        id: toastId,
        description: error?.message ?? 'Something went wrong.',
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit badge type' : 'Create badge type'}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? `btid ${badgeType?.btid}. Soft-disable with Active off — no hard delete.`
              : 'Define a credential that users or organizations can earn.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1 sm:col-span-2">
              <Label htmlFor="bt-name">Name</Label>
              <Input
                id="bt-name"
                value={form.name}
                onChange={(e) => setField('name', e.target.value)}
                required
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="bt-system">System name</Label>
              <Input
                id="bt-system"
                value={form.name_system}
                onChange={(e) => setField('name_system', e.target.value)}
                placeholder="independent_merchant"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="bt-category">Category</Label>
              <Input
                id="bt-category"
                value={form.category}
                onChange={(e) => setField('category', e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="bt-tier">Tier</Label>
              <Input
                id="bt-tier"
                value={form.tier}
                onChange={(e) => setField('tier', e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="bt-scope">Scope</Label>
              <select
                id="bt-scope"
                className={inputCls}
                value={form.scope}
                onChange={(e) => setField('scope', e.target.value)}
              >
                <option value="user">User</option>
                <option value="org">Organization</option>
                <option value="both">Both</option>
              </select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="bt-method">Award method</Label>
              <select
                id="bt-method"
                className={inputCls}
                value={form.award_method}
                onChange={(e) => setField('award_method', e.target.value)}
              >
                <option value="application">Application</option>
                <option value="manual">Manual</option>
                <option value="auto">Auto</option>
              </select>
            </div>
            <div className="space-y-1 sm:col-span-2">
              <Label htmlFor="bt-desc">Description</Label>
              <textarea
                id="bt-desc"
                rows={3}
                className={inputCls}
                value={form.description}
                onChange={(e) => setField('description', e.target.value)}
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={form.requires_verification}
                onChange={(e) => setField('requires_verification', e.target.checked)}
              />
              Requires verification
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) => setField('active', e.target.checked)}
              />
              Active
            </label>
          </div>

          {isEdit && badgeType ? <BadgeRulesPanel btid={badgeType.btid} /> : null}

          <div className="flex justify-end gap-2 border-t border-gray-100 pt-4">
            <Button type="button" variant="outline" onClick={handleClose} disabled={isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {isEdit ? 'Save changes' : 'Create'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default BadgeTypeFormModal;
